import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import queryString from 'query-string';
import lodashCompact from 'lodash/fp/compact';

import { USER_ROLE_ANONYMOUS, USER_ROLE_AGENT } from 'common/userRoles';
import { getListingUrlPath } from 'common/listingHelpers';
import { formatListingAddress } from 'common/formatters';
import { isUserAgentMobile } from 'common/util/isUserAgentMobileApp';

import { SCREEN_ID_ADDRESS_SEARCH } from 'client/constants/screenIds';
import { FEED_CATEGORY_AREA_TO_TOUR } from 'common/feedCategoryConstants';
import {
  SR_API_ACTION_GET_LISTING_BY_MLS_NUMBER,
  SR_API_ACTION_GOOGLE_MAPS_GET_PLACE_DETAILS,
  SR_API_ACTION_GOOGLE_MAPS_GET_AUTOCOMPLETE_PREDICTIONS,
} from 'api/actionTypes';

import {
  extractListingAddressFieldsFromGooglePlace,
  extractLocationMapCenterFromGooglePlace,
} from 'client/lib/googlePlaces';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';

import Navbar from 'client/components/Navbar';
import TabBar from 'client/components/TabBar';
import SrAgentDashboardTabBar from 'client/components/SrAgentDashboardTabBar';
import SrAddressSearchAutocomplete from 'client/components/SrAddressSearchAutocomplete';
import SrEducationalSearchCard from 'client/components/SrEducationalSearchCard';
import SrLink from 'client/components/SrLink/SrLink';
import classnames from 'classnames';

import ListingActions from 'client/actions/Listing';
import UserActions from 'client/actions/User';
import MarketActions from 'client/actions/Market';

import postWithState from 'client/actions/postWithState';
import SrContinueApplicationButton from 'client/components/SrContinueApplicationButton';

import branch from 'client/core/branch';
import log from 'client/lib/log';

import { getUserLandingPage } from 'client/helpers/allowedRoles';

const debug = log.debug('sunroom:pages:AddressSearch');

const cityLinks = [];

@withTransitionHook
@withScreenId(SCREEN_ID_ADDRESS_SEARCH)
@withScrollToTop
@withRouter
@branch({
  currentUserRoles: ['currentUser', 'roles'],
  recentSearchQueries: ['recentSearchQueries'],
  userAgent: ['analytics', 'userAgent'],
  educationalSearchCardClosed: ['currentUser', 'settings', 'educationalSearchCardClosed'],
  currentUserMarket: ['markets', 'currentUserMarket'],
  neighborhoodsByMarketId: ['markets', 'neighborhoodsByMarketId'],
})
class AddressSearchPage extends Component {
  static fetchData(state, inject) {
    const currentUser = inject.state.get(['currentUser']);
    const isAnonymous = currentUser.roles.includes(USER_ROLE_ANONYMOUS);
    const recentSearchQueriesLoaded = !!inject.state.get(['recentSearchQueries']);

    const promisesToResolve = [
      MarketActions.getNeighborhoodsByMarketId(inject, currentUser.searchFilters.marketId),
    ];

    if (!isAnonymous && !recentSearchQueriesLoaded) {
      promisesToResolve.push(UserActions.getSearchQueries(inject));
    }

    return Promise.all(promisesToResolve);
  }

  static getPageMetadata = () => ({
    pageTitle: 'Search | Sunroom Rentals',
    section: 'AddressSearch',
    keywords: 'sunroom, search',
    description: 'Search the best rentals in Texas. Available to rent now!',
  });

  static propTypes = {
    currentUserRoles: PropTypes.array.isRequired,
    recentSearchQueries: PropTypes.array,
    educationalSearchCardClosed: PropTypes.bool,
    userAgent: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    currentUserMarket: PropTypes.object.isRequired,
    neighborhoodsByMarketId: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      isAddressNotFound: false,
      value: '',
    };
    this._onSelect = this._onSelect.bind(this);
  }

  componentWillUnmount() {
    this._isUnmounting = true;
  }

  _onCityLinkClick = ({ id, mapCenter = {}, zoom }) => {
    const { history } = this.props;
    const locationQuery = queryString.stringify({
      mapCenterLat: mapCenter.lat,
      mapCenterLng: mapCenter.lng,
      mapZoomLevel: zoom,
    });
    history.push(`/feed/${FEED_CATEGORY_AREA_TO_TOUR}/map/${id}?${locationQuery}`);
  };

  _searchListingByMlsNumber = (mlsNumber) => {
    const { dispatch, history } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_GET_LISTING_BY_MLS_NUMBER,
      payload: {
        mlsNumber,
      },
      onSuccess: (listing) => {
        if (listing && !this._isUnmounting) {
          history.push(getListingUrlPath(listing));
        }
      },
    });
  };

  _onChange = (value) => {
    this.setState({
      isAddressNotFound: false,
      value: value,
    });

    if (!isNaN(value) && value.length === 7) {
      this._searchListingByMlsNumber(value);
    }
  };

  async _onSelect({ input, address, placeId }) {
    const { history, userAgent, dispatch } = this.props;

    debug('_onSelect', {
      input,
      address,
      placeId,
    });

    const isMobile = isUserAgentMobile(userAgent);

    const cityLinkMatch = cityLinks.find(({ name }) => {
      return input.toLowerCase() === name.toLowerCase();
    });

    if (cityLinkMatch) {
      this._onCityLinkClick({
        id: cityLinkMatch.id,
      });
      return;
    }

    this.setState({
      isLoading: true,
      isAddressNotFound: false,
    });

    try {
      let googlePlaces;
      if (placeId) {
        googlePlaces = lodashCompact([
          await dispatch(postWithState, {
            action: SR_API_ACTION_GOOGLE_MAPS_GET_PLACE_DETAILS,
            payload: {
              placeId,
            },
          }),
        ]);
      } else {
        const autocompletePredictions = await dispatch(postWithState, {
          queryId: address,
          action: SR_API_ACTION_GOOGLE_MAPS_GET_AUTOCOMPLETE_PREDICTIONS,
          payload: {
            input: address,
            limitToTexas: false,
          },
        });

        let googlePlace = null;
        if (autocompletePredictions.length > 0 && autocompletePredictions[0].place_id) {
          googlePlace = await dispatch(postWithState, {
            action: SR_API_ACTION_GOOGLE_MAPS_GET_PLACE_DETAILS,
            payload: {
              placeId: autocompletePredictions[0].place_id,
            },
          });
        }

        googlePlaces = googlePlace ? [googlePlace] : [];
      }
      if (this._isUnmounting) {
        return;
      }

      debug('Geocoded places', googlePlaces);

      if (!googlePlaces || googlePlaces.length === 0) {
        this.setState({
          isLoading: false,
          isAddressNotFound: true,
        });
        return;
      }
      // Request the listing ID by listingAddressFields
      // TODO: Event not tracking ZERO_RESULTS gmaps "error"
      const listingAddressFields = extractListingAddressFieldsFromGooglePlace(googlePlaces[0]);

      if (!listingAddressFields) {
        const locationMapCenter = extractLocationMapCenterFromGooglePlace(googlePlaces[0]);
        if (!locationMapCenter) {
          this.setState({
            isLoading: false,
            isAddressNotFound: true,
          });
          return;
        }

        this._onCityLinkClick({
          id: 'search',
          mapCenter: locationMapCenter,
          zoom: isMobile ? 14 : 15,
        });
        return;
      }

      const listing = await dispatch(ListingActions.getByAddressFields, listingAddressFields);
      if (this._isUnmounting) {
        return;
      }
      if (!listing) {
        this.setState({
          isLoading: false,
          isAddressNotFound: true,
        });
        return;
      }

      await dispatch(UserActions.getSearchQueries);

      history.push(getListingUrlPath(listing));
    } catch (error) {
      log.error(error);
      if (!this._isUnmounting) {
        this.setState({
          isLoading: false,
        });
      }
    }
  }

  render() {
    const {
      currentUserRoles,
      recentSearchQueries,
      educationalSearchCardClosed,
      currentUserMarket,
      neighborhoodsByMarketId,
    } = this.props;

    const { isLoading, isAddressNotFound, value } = this.state;

    const isAgent = currentUserRoles.includes(USER_ROLE_AGENT);
    const notFoundText = "We can't find this home...";
    const neighborhoods = neighborhoodsByMarketId[currentUserMarket.id] || [];

    return (
      <div className="pageAddressSearch">
        <div className={classnames('mainContainer', !value ? null : 'isActive')}>
          <Navbar redirectLogoTo={getUserLandingPage({ userRoles: currentUserRoles })} />
          <div>
            <span className="headline main">Search the {currentUserMarket.displayNameFull}</span>
          </div>
          <SrAddressSearchAutocomplete
            onSelect={this._onSelect}
            onChange={this._onChange}
            disabled={isLoading}
            currentUserMarket={currentUserMarket}
          />
          {!currentUserRoles.includes(USER_ROLE_ANONYMOUS) && <SrContinueApplicationButton />}
          {isAgent ? (
            <SrAgentDashboardTabBar />
          ) : (
            !currentUserRoles.includes(USER_ROLE_ANONYMOUS) && <TabBar />
          )}
          {!educationalSearchCardClosed && !isAgent && (
            <SrEducationalSearchCard currentUserMarket={currentUserMarket} />
          )}
          {isAddressNotFound && (
            <div className="noResults">
              <div className="houseSad" />
              <span className="explainerText">{notFoundText}</span>
            </div>
          )}
          {!isLoading && recentSearchQueries && recentSearchQueries.length > 0 && (
            <div className="sectionLinksContainer recentSearches">
              <span className="headline">recent searches</span>
              {recentSearchQueries.map((searchQuery) => (
                <div key={searchQuery.id}>
                  <SrLink to={getListingUrlPath(searchQuery.listing)} className="recentLink">
                    {formatListingAddress(searchQuery.listing)}
                  </SrLink>
                </div>
              ))}
            </div>
          )}
          {!isAgent && neighborhoods.length >= 1 && (
            <div className="sectionLinksContainer cities">
              <span className="headline">areas to tour</span>
              {neighborhoods.map((neighborhood) => (
                <div key={neighborhood.id}>
                  <span
                    onClick={() =>
                      this._onCityLinkClick({
                        id: neighborhood.id,
                      })
                    }
                    className="recentLink"
                  >
                    {neighborhood.name}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
}

AddressSearchPage.displayName = 'AddressSearchPage';

export default AddressSearchPage;
